import React, { useState } from 'react';
import { Modal, Upload, Table, Button, message, Alert, Space, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import {
  supplierApi,
  type SupplierDTO,
  type SupplierImportAppendItemDTO,
  type SupplierImportPreviewDTO,
} from '../api/kfc';

interface SupplierImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const contactLabel = (c: { contactName?: string; phoneNumber?: string; remark?: string }) =>
  [c.contactName, c.phoneNumber, c.remark].filter(Boolean).join(' / ') || '-';

const SupplierImportModal: React.FC<SupplierImportModalProps> = ({ open, onClose, onSuccess }) => {
  const [preview, setPreview] = useState<SupplierImportPreviewDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const reset = () => {
    setPreview(null);
    setLoading(false);
    setConfirming(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleUpload = (file: File) => {
    setLoading(true);
    supplierApi
      .importPreview(file)
      .then((res) => {
        setPreview(res.data);
        const s = res.data.summary;
        message.info(
          `解析完成：新增 ${s.create}，追加联系人 ${s.appendContact}，无变化 ${s.unchanged}，错误 ${s.error}`
        );
      })
      .catch((e) => message.error(e.response?.data?.message || '解析失败'))
      .finally(() => setLoading(false));
    return false;
  };

  const handleConfirm = () => {
    if (!preview) return;
    const toCreate = preview.toCreate || [];
    const toAppendContact = preview.toAppendContact || [];
    if (!toCreate.length && !toAppendContact.length) {
      message.info('没有需要导入的变更');
      return;
    }
    setConfirming(true);
    supplierApi
      .importConfirm({ toCreate, toAppendContact })
      .then((res) => {
        message.success(
          `导入完成：新增供应商 ${res.data.created}，追加联系人 ${res.data.appendedContact}`
        );
        handleClose();
        onSuccess();
      })
      .catch((e) => message.error(e.response?.data?.message || '导入失败'))
      .finally(() => setConfirming(false));
  };

  return (
    <Modal
      title="导入供应商"
      open={open}
      onCancel={handleClose}
      width={920}
      footer={
        preview ? (
          <Space>
            <Button onClick={() => setPreview(null)}>重新选择文件</Button>
            <Button onClick={handleClose}>取消</Button>
            <Button
              type="primary"
              loading={confirming}
              disabled={!preview.toCreate?.length && !preview.toAppendContact?.length}
              onClick={handleConfirm}
            >
              确认导入
            </Button>
          </Space>
        ) : (
          <Button onClick={handleClose}>关闭</Button>
        )
      }
    >
      {!preview ? (
        <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleUpload}>
          <Button icon={<UploadOutlined />} loading={loading}>
            选择 Excel 文件
          </Button>
        </Upload>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <Typography.Text>
                新增 {preview.summary.create} 个供应商，追加联系人 {preview.summary.appendContact} 个，无变化{' '}
                {preview.summary.unchanged} 个
                {preview.summary.error > 0 ? `，错误 ${preview.summary.error} 条` : ''}
              </Typography.Text>
            }
          />
          {preview.errors?.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="以下行无法导入"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {preview.errors.map((e, i) => (
                    <li key={`${e.row}-${i}`}>
                      第 {e.row} 行：{e.message}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
          {preview.toCreate?.length > 0 && (
            <>
              <Typography.Title level={5}>新增供应商（{preview.toCreate.length}）</Typography.Title>
              <Table
                size="small"
                rowKey={(r: SupplierDTO) => r.supplierId || ''}
                dataSource={preview.toCreate}
                pagination={{ pageSize: 5 }}
                scroll={{ x: 800 }}
                columns={[
                  { title: '供应商标识', dataIndex: 'supplierId', width: 120 },
                  { title: '供应商名称', dataIndex: 'supplierName', width: 140, ellipsis: true },
                  { title: '地址', dataIndex: 'address', width: 160, ellipsis: true },
                  {
                    title: '联系人数',
                    key: 'contactCount',
                    width: 90,
                    render: (_, r) => r.contacts?.length || 0,
                  },
                ]}
                style={{ marginBottom: 16 }}
              />
            </>
          )}
          {preview.toAppendContact?.length > 0 && (
            <>
              <Typography.Title level={5}>追加联系人（{preview.toAppendContact.length}）</Typography.Title>
              <Table
                size="small"
                rowKey={(r: SupplierImportAppendItemDTO) => r.id || r.supplierId || Math.random().toString()}
                dataSource={preview.toAppendContact}
                pagination={{ pageSize: 5 }}
                scroll={{ x: 800 }}
                columns={[
                  { title: '供应商标识', dataIndex: 'supplierId', width: 120 },
                  { title: '供应商名称', dataIndex: 'supplierName', width: 140, ellipsis: true },
                  {
                    title: '新增联系人',
                    key: 'contacts',
                    render: (_, r) => (r.contacts || []).map((c, i) => <div key={i}>{contactLabel(c)}</div>),
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default SupplierImportModal;
